import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { MapPin, Ship, Calendar, Hash, X, ChevronRight, DollarSign, Filter, Clock } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getPreloadedCoordinates } from '../utils/zipCodes';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

// Custom marker icon
const createCustomIcon = (count = 1) => {
  const color = count > 5 ? '#D0332A' : count > 1 ? '#C78F3C' : '#4a4a4a';
  
  if (count > 1) {
    // Cluster icon
    return L.divIcon({
      html: `
        <div style="
          background: ${color};
          color: white;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          font-weight: bold;
          font-size: 16px;
        ">
          ${count}
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [0, -20],
      className: 'custom-cluster-icon'
    });
  } else {
    // Single boat icon
    return L.divIcon({
      html: `
        <div style="
          background: ${color};
          color: white;
          border-radius: 50% 50% 50% 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          transform: rotate(-45deg);
          position: relative;
        ">
          <div style="transform: rotate(45deg);">⚓</div>
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32],
      className: 'custom-boat-icon'
    });
  }
};

// Component to fit bounds to all markers
const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      const leafletBounds = L.latLngBounds(bounds);
      map.fitBounds(leafletBounds, { padding: [50, 50], maxZoom: 10 });
    }
  }, [bounds, map]);
  return null;
};

const MapView = ({ boats, onBoatClick, onLocationFilter }) => {
  const [locationData, setLocationData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Center of USA
  const defaultCenter = [39.8283, -98.5795];
  
  // Calculate map bounds
  const mapBounds = Object.values(locationData)
    .filter(loc => loc.coordinates)
    .map(loc => [loc.coordinates.lat, loc.coordinates.lng]);

  // Get coordinates for a zip code
  const getCoordinatesForZip = useCallback((zip) => {
    if (!zip) return null;
    const coords = getPreloadedCoordinates(zip);
    if (coords) return coords;
    return null;
  }, []);

  // Process boats and group by location
  useEffect(() => {
    const processBoats = async () => {
      if (boats.length === 0) {
        setLocationData({});
        return;
      }

      const newLocationData = {};
      let preloadedCount = 0;
      let geocodedCount = 0;
      let failedCount = 0;
      
      for (const boat of boats) {
        const zip = boat.zipCode?.trim();
        
        if (!zip) {
          failedCount++;
          continue;
        }
        
        // Create a unique key for this location
        const locationKey = zip;
        
        if (!newLocationData[locationKey]) {
          // Try to get coordinates
          const coords = getCoordinatesForZip(zip);
          
          if (coords) {
            preloadedCount++;
            newLocationData[locationKey] = {
              zip: zip,
              coordinates: coords,
              boats: []
            };
          } else {
            failedCount++;
            continue;
          }
        }
        
        // Add boat to this location
        if (newLocationData[locationKey]) {
          newLocationData[locationKey].boats.push(boat);
        }
      }
      
      console.log(`📍 Map locations - Preloaded: ${preloadedCount}, Geocoded: ${geocodedCount}, Failed: ${failedCount}`);
      setLocationData(newLocationData);
    };
    
    processBoats();
  }, [boats, getCoordinatesForZip]);

  const formatPrice = (price) => {
    if (!price) return 'Contact for Price';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleMarkerClick = (location) => {
    setSelectedLocation(location);
    setSidebarOpen(true);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setTimeout(() => setSelectedLocation(null), 300);
  };

  return (
    <div className="map-view-wrapper">
      <div className={`map-container-split ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {/* Map Section */}
        <div className="map-section">
          {loading && (
            <div className="map-loading-overlay">
              <div className="map-loading-content">
                <MapPin className="animate-pulse" />
                <span>Loading boat locations...</span>
              </div>
            </div>
          )}
          
          <MapContainer
            center={defaultCenter}
            zoom={4}
            style={{ height: '600px', width: '100%' }}
            className="boat-map"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            
            <FitBounds bounds={mapBounds} />
            
            {Object.entries(locationData).map(([key, location]) => (
              <Marker
                key={key}
                position={[location.coordinates.lat, location.coordinates.lng]}
                icon={createCustomIcon(location.boats.length)}
                eventHandlers={{
                  click: () => handleMarkerClick(location)
                }}
              >
                <Popup maxWidth={200} closeButton={false}>
                  <div className="map-popup-mini">
                    <h4>{location.zip}</h4>
                    <p>{location.boats.length} boat{location.boats.length !== 1 ? 's' : ''}</p>
                    <button 
                      className="view-boats-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkerClick(location);
                      }}
                    >
                      View Details
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Sidebar Panel */}
        <div className={`map-sidebar-panel ${sidebarOpen ? 'open' : ''}`}>
          {selectedLocation && (
            <>
              {/* Sidebar Header */}
              <div className="sidebar-header">
                <div className="location-info">
                  <MapPin size={20} className="location-icon" />
                  <div>
                    <h3>Location: {selectedLocation.zip}</h3>
                    <p>{selectedLocation.boats.length} boats available</p>
                  </div>
                </div>
                <button className="close-btn" onClick={closeSidebar}>
                  <X size={24} />
                </button>
              </div>

              {/* Action Buttons */}
              <div className="sidebar-actions">
                {onLocationFilter && (
                  <button
                    className="filter-btn"
                    onClick={() => {
                      onLocationFilter(selectedLocation.zip);
                      closeSidebar();
                    }}
                  >
                    <Filter size={16} />
                    Filter to This Location
                  </button>
                )}
              </div>

              {/* Boat List */}
              <div className="boat-list-container">
                {selectedLocation.boats.map((boat, idx) => (
                  <div
                    key={boat.id}
                    className="boat-list-item"
                    onClick={() => {
                      onBoatClick(boat);
                      closeSidebar();
                    }}
                  >
                    <div className="boat-thumb">
                      {boat.imageUrls && boat.imageUrls.length > 0 ? (
                        <>
                          <img 
                            src={boat.imageUrls[0]} 
                            alt={boat.title}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="thumb-fallback" style={{display: 'none'}}>
                            <Ship size={24} />
                          </div>
                        </>
                      ) : (
                        <div className="thumb-fallback">
                          <Ship size={24} />
                        </div>
                      )}
                      {boat.imageUrls && boat.imageUrls.length > 1 && (
                        <span className="image-count">+{boat.imageUrls.length - 1}</span>
                      )}
                    </div>

                    <div className="boat-info">
                      <h4>{boat.year} {boat.model || boat.title}</h4>
                      <div className="boat-meta">
                        {boat.hours !== undefined && (
                          <span>
                            <Clock size={12} />
                            {boat.hours} hrs
                          </span>
                        )}
                        <span>
                          <Hash size={12} />
                          {boat.stocknumber || 'N/A'}
                        </span>
                        {boat.imageUrls && (
                          <span>
                            📸 {boat.imageUrls.length}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="boat-price">
                      {formatPrice(boat.price)}
                    </div>

                    <ChevronRight size={18} className="boat-chevron" />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile Bottom Sheet */}
      <div className={`mobile-bottom-sheet ${sidebarOpen ? 'open' : ''}`}>
        {selectedLocation && (
          <>
            <div className="sheet-handle" onClick={closeSidebar}></div>
            <div className="sheet-header">
              <h3>
                <MapPin size={18} />
                {selectedLocation.zip} - {selectedLocation.boats.length} boats
              </h3>
              <button onClick={closeSidebar}>
                <X size={20} />
              </button>
            </div>
            <div className="sheet-content">
              {selectedLocation.boats.map((boat) => (
                <div
                  key={boat.id}
                  className="sheet-boat-item"
                  onClick={() => {
                    onBoatClick(boat);
                    closeSidebar();
                  }}
                >
                  <div className="boat-main-info">
                    <h5>{boat.year} {boat.model || boat.title}</h5>
                    <span className="boat-price">{formatPrice(boat.price)}</span>
                  </div>
                  <ChevronRight size={16} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MapView;
